// src/components/layout/Navbar.js
import React, { useContext, useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import AuthContext from '../../context/auth/authContext';
import ThemeContext from '../../context/theme/themeContext';
import NotificationBell from './NotificationBell';
import ThemeToggle from './ThemeToggle';
import MatchTypeModal from '../matches/MatchTypeModal';
import './Navbar.css';

const Navbar = () => {
  const { isAuthenticated, logout, user } = useContext(AuthContext);
  const { isDarkMode } = useContext(ThemeContext);
  const [showMatchTypeModal, setShowMatchTypeModal] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const showTCG = user?.gamePreferences ? user.gamePreferences.tcg === true : true;
  const showVGC = user?.gamePreferences ? user.gamePreferences.vgc === true : true;
  const isAdmin = user && (user.isAdmin || user.role === 'admin' || user.role === 'owner' || user.isOwner);

  const isActive = (path) => (location.pathname === path ? 'active' : '');
  const inSection = (frag) => (location.pathname.includes(frag) ? 'active' : '');

  // Shrink-on-scroll: condense the bar + swap to the compact mark once scrolled.
  useEffect(() => {
    let frame = null;
    const onScroll = () => {
      if (frame !== null) return;
      frame = window.requestAnimationFrame(() => {
        frame = null;
        setScrolled(window.scrollY > 12);
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => {
      if (frame !== null) window.cancelAnimationFrame(frame);
      window.removeEventListener('scroll', onScroll);
    };
  }, []);

  // Close the avatar menu on route change.
  useEffect(() => { setMenuOpen(false); }, [location.pathname]);

  const onLogout = () => {
    localStorage.removeItem('userMatchCounts');
    logout();
    window.location.reload();
  };

  const logoSrc = isDarkMode ? '/Logo-with-slogan-darkmode.png' : '/Logo-with-slogan.png';

  const navLinks = (
    <ul className="nav-menu">
      <li><Link to="/dashboard" className={`nav-link ${isActive('/dashboard')}`}>Dashboard</Link></li>
      {showTCG && (
        <li className="dropdown">
          <button className={`nav-link dropdown-btn ${inSection('/analytics/tcg')}`}>TCG <i className="fas fa-chevron-down" /></button>
          <ul className="dropdown-menu">
            <li><button className={`dropdown-item ${inSection('/analytics/tcg')}`} onClick={() => navigate('/analytics/tcg/matches')}><i className="fas fa-chart-line" /> TCG Analytics</button></li>
            <li><button className={`dropdown-item ${isActive('/analytics/global')}`} onClick={() => navigate('/analytics/global')}><i className="fas fa-globe" /> Global Analytics</button></li>
            <hr className="dropdown-divider" />
            <li><button className={`dropdown-item ${isActive('/lists')}`} onClick={() => navigate('/lists')}><i className="fas fa-list" /> My TCG Decks</button></li>
          </ul>
        </li>
      )}
      {showVGC && (
        <li className="dropdown">
          <button className={`nav-link dropdown-btn ${inSection('/analytics/vgc')}`}>VGC <i className="fas fa-chevron-down" /></button>
          <ul className="dropdown-menu">
            <li><button className={`dropdown-item ${inSection('/analytics/vgc')}`} onClick={() => navigate('/analytics/vgc/matches')}><i className="fas fa-gamepad" /> VGC Analytics</button></li>
            <hr className="dropdown-divider" />
            <li><button className={`dropdown-item ${isActive('/lists-vgc')}`} onClick={() => navigate('/lists-vgc')}><i className="fas fa-shield-alt" /> My VGC Teams</button></li>
          </ul>
        </li>
      )}
      {isAuthenticated && (
        <li className="dropdown">
          <button className={`nav-link dropdown-btn ${inSection('/tournament')}`}>Tournaments <i className="fas fa-chevron-down" /></button>
          <ul className="dropdown-menu">
            <li><button className={`dropdown-item ${isActive('/tournament/analytics')}`} onClick={() => navigate('/tournament/analytics')}><i className="fas fa-chart-bar" /> Analytics</button></li>
            <li><button className={`dropdown-item ${isActive('/tournament/my-analytics')}`} onClick={() => navigate('/tournament/my-analytics')}><i className="fas fa-user" /> My Analytics</button></li>
            <hr className="dropdown-divider" />
            <li><button className={`dropdown-item ${isActive('/tournament/decks')}`} onClick={() => navigate('/tournament/decks')}><i className="fas fa-layer-group" /> Decks</button></li>
          </ul>
        </li>
      )}
    </ul>
  );

  return (
    <>
      <nav className={`navbar desktop-only ${scrolled ? 'is-scrolled' : ''}`}>
        <div className="navbar-container">
          <Link to={isAuthenticated ? '/dashboard' : '/'} className="brand">
            <img src={logoSrc} alt="PokéMatch" className="brand-logo" />
            <img src="/logo.png" alt="PokéMatch" className="brand-mark" />
          </Link>

          <div className="nav-links">{navLinks}</div>

          <div className="nav-actions">
            <button className="btn btn-primary log-match-btn" onClick={() => setShowMatchTypeModal(true)}>
              <i className="fas fa-plus" /> Log Match
            </button>

            {isAuthenticated ? (
              <>
                <div className="navbar-notification"><NotificationBell /></div>
                <div className="avatar-wrap">
                  <button className="avatar-btn" onClick={() => setMenuOpen((v) => !v)} aria-label="Account menu">
                    {user?.pokemonSprite
                      ? <img src={user.pokemonSprite} alt="" className="avatar-img" />
                      : <i className="fas fa-user" />}
                  </button>
                  {menuOpen && (
                    <>
                      <div className="avatar-scrim" onClick={() => setMenuOpen(false)} />
                      <div className="avatar-menu" role="menu">
                        <div className="avatar-menu__head">
                          <span className="avatar-menu__name">{user?.name}</span>
                          <span className="avatar-menu__sub">@{user?.username}</span>
                        </div>
                        <Link className="avatar-menu__item" to="/profile-settings"><i className="fas fa-user-cog" /> Profile &amp; Settings</Link>
                        <Link className="avatar-menu__item" to="/friends"><i className="fas fa-users" /> Friends</Link>
                        <Link className="avatar-menu__item" to="/patch-notes"><i className="fas fa-code-branch" /> Patch Notes</Link>
                        <Link className="avatar-menu__item" to="/api-docs"><i className="fas fa-book" /> API Docs</Link>
                        <Link className="avatar-menu__item" to="/support"><i className="fas fa-headset" /> Support</Link>
                        <a className="avatar-menu__item" href="https://discord.gg/CWe3CtaCCs" target="_blank" rel="noopener noreferrer"><i className="fab fa-discord" /> Discord</a>
                        <div className="avatar-menu__row">
                          <span><i className="fas fa-adjust" /> Dark mode</span>
                          <ThemeToggle />
                        </div>
                        {isAdmin && (
                          <>
                            <div className="avatar-menu__divider" />
                            <Link className="avatar-menu__item avatar-menu__item--admin" to="/admin"><i className="fas fa-shield-halved" /> Admin</Link>
                          </>
                        )}
                        <div className="avatar-menu__divider" />
                        <button className="avatar-menu__item avatar-menu__item--danger" onClick={onLogout}><i className="fas fa-sign-out-alt" /> Log out</button>
                      </div>
                    </>
                  )}
                </div>
              </>
            ) : (
              <>
                <ThemeToggle />
                <Link to="/login" className="btn btn-outline">Login</Link>
                <Link to="/register" className="btn btn-primary">Register</Link>
              </>
            )}
          </div>
        </div>
      </nav>
      {showMatchTypeModal && <MatchTypeModal onClose={() => setShowMatchTypeModal(false)} />}
    </>
  );
};

export default Navbar;
